// Operator-ui mirror of the N10 frozen BFF contract
// (`apps/operator-bff/src/modules/notifications/notifications.types.ts`). These
// are the EXACT shapes the operator surface (N11) consumes off the operator-bff:
//   GET  /v1/notifications/case/:case_urn            → INotificationCaseStatus
//   POST /v1/notifications/:case_urn/send            → ISendNotificationResult
//   POST /v1/cases/:case_urn/sub-cases/:sub/force-accept → IForceAcceptResult
// Mirrored (not imported) because operator-ui and operator-bff don't share a
// build; keep these names byte-identical to the BFF — additive evolution only.

/**
 * Outbound channel a passenger offer notification rode (PNR-group level).
 * `VOICE` = the Twilio TTS call with DTMF 1/2 accept/reject (N17 / NEX-430).
 */
export type NotificationChannel = 'SMS' | 'WHATSAPP' | 'EMAIL' | 'VOICE' | 'PUSH';

/** What the passenger did with what we sent them (cases `passengerEngagement`). */
export type PassengerEngagementKind = 'LINK_OPENED' | 'APP_OPENED' | 'OFFER_VIEWED';

/** One observation on the card's timeline: the kind and when. */
export interface INotificationEngagementEvent {
  kind: PassengerEngagementKind;
  /** ISO-8601. */
  at: string;
}

/** Observed delivery state; unknown when there is no delivery evidence. */
export type NotificationDeliveryState =
  | 'unknown'
  | 'pending'
  | 'sent'
  | 'delivered'
  | 'failed';

/** Acceptance-gate state, derived from the cases sub-case FSM (SEAM-1). */
export type NotificationAcceptanceState = 'none' | 'awaiting' | 'accepted' | 'declined';

/**
 * One notification send in the history for a PNR group. Fixes #615:
 * the operator can now see ALL notification sends (SMS/email/etc.),
 * not just the latest one.
 */
export interface INotificationSendRow {
  notificationUrn: string;
  channel: NotificationChannel;
  deliveryState: NotificationDeliveryState;
  requestedAction?: 'ACCEPT_OFFER' | 'CONTACT_DESK' | 'NONE';
  lastEventAt: string;
  failureReason?: string;
  /** ISO-8601 — when this send left us; a later receipt never moves it. */
  sentAt?: string;
  /** ISO-8601 — first read: an email open (never a privacy proxy's) or a WhatsApp read receipt. */
  readAt?: string;
  /** ISO-8601 — first click on a link in this email. */
  clickedAt?: string;
  /** The passenger advisory this send was (`OFFER_EXPIRED`: «the offer is no longer available,
   *  its flight left», `FLIGHT_PURCHASE_FAILED`…). Absent on offers and status messages. */
  advisoryKind?: string;
}

/** One row per PNR group (= one sub-case = the notification recipient unit). */
export interface INotificationStatusRow {
  /** Recipient identity — the PNR group's lead passenger URN. */
  paxRef: string;
  /** Friendly label (lead passenger name); URNs stay secondary (§18). */
  displayName: string | null;
  /** The actionable sub-case (PNR group) — the force-accept / resend target. */
  subCaseUrn: string;
  /** The PNR URN, when the sub-case carries one. */
  pnrUrn: string | null;
  /** Passengers in this PNR group (a group can hold a family). */
  passengerCount: number;
  /** Outbound channel (group preferred channel; `null` = template default). */
  channel: NotificationChannel | null;
  /** Delivery telemetry; never inferred from acceptance. */
  deliveryState: NotificationDeliveryState;
  /** Acceptance-gate state from the cases sub-case status. */
  acceptanceState: NotificationAcceptanceState;
  /** ISO-8601 of the last delivery/acceptance event (M1: sub-case `updatedAt`). */
  lastEventAt: string | null;
  /** SEAM-1 idempotency/staleness key for force-accept. `null` in M1. */
  offerHash: string | null;
  notificationUrn?: string;
  /** Safe delivery failure code, never provider text or contact data. */
  failureReason?: string;
  requestedAction?: 'ACCEPT_OFFER' | 'CONTACT_DESK' | 'NONE';
  /** ISO-8601 — the passenger opened a link we sent, after the latest send. The
   *  message reached them (`deliveryState` is then `delivered`). */
  linkOpenedAt?: string;
  /** ISO-8601 — the passenger app showed their trip after the latest send, however
   *  they got in (the link, a PNR sign-in, a returning session). Reached as well. */
  appOpenedAt?: string;
  /** ISO-8601 — an offer awaiting their answer was on the passenger's screen after
   *  the latest send: «vio la oferta» as a fact. Reached as well. */
  offerViewedAt?: string;
  /** What the passenger did with what we sent them, newest first (at most 10). */
  engagement?: INotificationEngagementEvent[];
  /** ISO-8601 — the passenger read the current message (email open, WhatsApp read receipt). */
  readAt?: string;
  /** ISO-8601 — the passenger clicked a link in the current email. */
  clickedAt?: string;
  /**
   * Full notification send history (fixes #615).
   * Contains ALL sends (SMS/email/etc.), newest first.
   */
  sends?: INotificationSendRow[];
}

/** Envelope for `GET /v1/notifications/case/:case_urn`. */
export interface INotificationCaseStatus {
  caseUrn: string;
  items: INotificationStatusRow[];
  deliveryStatusAvailable?: boolean;
}

/** Body of `POST /v1/notifications/:case_urn/send` (resend / choose channel). */
export interface ISendNotificationBody {
  /** Target a single PNR group; omit to (re-)dispatch the whole case. */
  subCaseUrn?: string;
  /** Override the channel for this dispatch; omit to honour the group preference. */
  channel?: NotificationChannel;
}

/** Result of a resend request (202). */
export interface ISendNotificationResult {
  status: 'queued' | 'nothing_to_send' | 'unavailable';
  notificationUrns?: string[];
  queued?: number;
  caseUrn: string;
  subCaseUrn?: string;
  channel?: NotificationChannel;
}

/** Body of the operator force-accept route (N11 → BFF). */
export interface IForceAcceptBody {
  /** SEAM-1 offer hash — REQUIRED for the gate's staleness check. */
  offerHash: string;
  /** Optional operator justification, audited on the canonical event. */
  reason?: string;
}

/** Result of force-accept (202). */
export interface IForceAcceptResult {
  status: 'accepted';
  subCaseUrn: string;
  forced: true;
}

/** The acceptance-state values the roster filter offers (excludes pre-gate `none`). */
export type AcceptanceFilter = 'all' | 'awaiting' | 'accepted' | 'declined';

/** The channel choices the resend menu offers. */
export const NOTIFICATION_CHANNELS: NotificationChannel[] = [
  'SMS',
  'WHATSAPP',
  'EMAIL',
  'VOICE',
  'PUSH',
];
